/**
 * Domino-specific gameplay messages + snapshot, layered on top of the shared
 * envelope from `@bn-games/network-protocol`. Mirror of
 * `packages/game-sessions/src/domino/protocol.ts` — keep both in sync.
 *
 * Unlike checkers/backgammon (pure-relay), the server is **authoritative** for
 * dominoes: it owns the shuffled boneyard, each seat's hand, and the Muggins
 * timer. Clients send move *intents* and apply server-authoritative snapshots
 * (filtered per seat via `getSeatView` so opponents' tiles never leave the
 * server).
 */

import type {
  LobbyClientMessage,
  LobbyServerMessage,
  StateSyncMessage as EnvelopeStateSyncMessage,
} from '@bn-games/network-protocol';
import type { Chain, DominoPlayer, LastAction, Tile, Turn } from '../game/types.js';
import type { GameStateData, PendingMuggins, HandResultRecord } from '../game/GameState.js';
import type { TeamMap } from '../game/types.js';
import { normalizeVariantId, type VariantId } from '../game/variants/index.js';
import type { PlayerCount } from '../game/variants/types.js';
import { seatsFor } from '../game/types.js';

export const DOMINO_VARIANTS: readonly VariantId[] = [
  'goat',
  'goat-2v2',
  'all-fives',
  'all-fives-2v2',
  'telefon',
  'telefon-2v2',
];

/**
 * Narrow the optional `variant` field on shared lobby messages to a known
 * domino VariantId, mapping legacy ids (`kozyol` → `telefon`) that rooms made
 * by older clients still carry. Defaults to `'goat'` (the current default)
 * when the server didn't supply a value, matching the servers' own fallback.
 */
export function parseVariant(field: string | undefined): VariantId {
  return normalizeVariantId(field) ?? 'goat';
}

/**
 * The snapshot's variant, legacy ids mapped. A server built before the
 * Телефон rename still sends `ruleSetId: 'kozyol'` while a deploy is rolling
 * out, and `getRuleSet` has no entry for it.
 */
export function snapshotVariant(snapshot: Pick<DominoSnapshot, 'ruleSetId'>): VariantId {
  return parseVariant(snapshot.ruleSetId);
}

export type Player = DominoPlayer;

/**
 * drand-derived proof bundle for a verifiable domino hand. One per hand
 * (attached to the snapshot at deal time + carried through history). Shared
 * with the backgammon roll proof via `@bn-games/verifiable-rng`.
 */
import type { DiceRollProof } from '@bn-games/verifiable-rng';
export type { DiceRollProof };

/**
 * The shared protocol carries a numeric `seat`; domino maps seat→position via
 * `seatsFor(playerCount)`. Unlike checkers/backgammon, the array length varies
 * with the player count — callers must thread playerCount through.
 */
export function playerForSeat(seat: number, playerCount: PlayerCount): Player {
  const seats = seatsFor(playerCount);
  const player = seats[seat];
  if (player === undefined) {
    throw new Error(`playerForSeat: seat ${seat} out of range for playerCount ${playerCount}`);
  }
  return player;
}

export type {
  ErrorCode,
  ReconnectFailReason,
  SeatedPlayer,
  CreateRoomMessage,
  RoomCreatedMessage,
  JoinRoomMessage,
  RoomJoinedMessage,
  OpponentJoinedMessage,
  GameStartMessage,
  RematchRequestMessage,
  RematchAcceptMessage,
  RematchDeclineMessage,
  RematchStartMessage,
  OpponentDisconnectedMessage,
  OpponentReconnectedMessage,
  ReconnectRoomMessage,
  ReconnectOkMessage,
  ReconnectFailMessage,
  RequestStateMessage,
  ErrorMessage,
  PingMessage,
  PongMessage,
} from '@bn-games/network-protocol';

// --- Gameplay (domino) — client-originated intents ---

export interface DominoPlayPayload {
  readonly tile: Tile;
  /** -1 for the opening lead, otherwise the arm index. */
  readonly armIndex: number;
  readonly turn: Turn;
}

export interface DominoPlayMessage {
  readonly type: 'DOMINO_PLAY';
  readonly play: DominoPlayPayload;
}

export interface DominoDrawMessage {
  readonly type: 'DOMINO_DRAW';
}

export interface DominoPassMessage {
  readonly type: 'DOMINO_PASS';
}

export interface DominoMugginsClaimMessage {
  readonly type: 'DOMINO_MUGGINS_CLAIM';
}

export interface DominoNextHandMessage {
  readonly type: 'DOMINO_NEXT_HAND';
}

// --- Gameplay (domino) — server-originated terminals ---

export interface DominoGameOverMessage {
  readonly type: 'DOMINO_GAME_OVER';
  readonly winnerSeats: readonly number[];
  readonly finalScores: readonly number[];
}

// --- Snapshot ---

/**
 * Wire shape of a single arm in the chain. We send `Tile[]` and `openValue`
 * verbatim — the chain object is already serializable.
 */
export interface SerializedArm {
  readonly tiles: readonly Tile[];
  readonly openValue: number;
}

export interface SerializedChain {
  readonly lead: Tile | null;
  readonly spinner: Tile | null;
  readonly arms: readonly SerializedArm[];
  readonly activeArms: readonly number[];
}

/**
 * Full game-state snapshot. The server applies `getSeatView(snapshot, seat)`
 * before sending, replacing every *other* seat's hand with a redacted stub
 * `{ count }` and dropping the boneyard composition — recipients only see
 * their own hand contents.
 *
 * `handsSeatView` is one entry per seat, position-matched with the engine's
 * `state.hands`. The recipient seat carries `tiles`; every other seat carries
 * only the count (so opponents' tiles never leave the server).
 */
export type SeatHandView =
  | { readonly kind: 'own'; readonly tiles: readonly Tile[] }
  | { readonly kind: 'hidden'; readonly count: number };

export interface DominoSnapshot {
  readonly ruleSetId: VariantId;
  readonly playerCount: PlayerCount;
  readonly pointGoal: number;
  readonly mugginsStrict: boolean;
  /**
   * Козёл toggles (always false for fives variants). Optional, like
   * `carriedPenalty`, so a snapshot from an older server still parses.
   */
  readonly opening13?: boolean;
  readonly baldFinish?: boolean;
  readonly seed: number;
  readonly handNumber: number;
  readonly handsSeatView: readonly SeatHandView[];
  /** Stripped to its size for recipients — composition stays on the server. */
  readonly boneyardCount: number;
  readonly chain: SerializedChain;
  readonly currentSeat: number;
  readonly leaderSeat: number;
  readonly scores: readonly number[];
  readonly teamMap: TeamMap;
  readonly pendingMuggins: PendingMuggins | null;
  readonly handOver: boolean;
  readonly matchOver: boolean;
  readonly lastResult: HandResultRecord | null;
  readonly consecutivePasses: number;
  readonly forcedLeadTile: Tile | null;
  /**
   * Козёл «яйца»: penalty points waiting for the loser of the next decided
   * hand. Public information, so the seat-view filter passes it through.
   */
  readonly carriedPenalty?: number;
  /**
   * Most recent in-hand engine action (play / draw / pass / muggins claim).
   * Drives the "what just happened" prefix on the turn banner. Optional so
   * older snapshots still parse cleanly during a rolling rollout.
   */
  readonly lastAction?: LastAction | null;
  /**
   * Per-arm visual turn (fold) state, position-matched to `chain.arms`. The
   * server tracks this from the `turn` field on every PLAY message so replays
   * (and mid-game reconnects) render the same chain geometry on every client.
   */
  readonly chainTurns: readonly (readonly Turn[])[];
  /**
   * Proof bundle for the current hand's drand-derived shuffle seed when the
   * match is running in verifiable mode (Phase B). Absent on non-verifiable
   * matches and on legacy snapshots. The seat-view filter passes it through
   * untouched — it identifies the randomness source, not any seat's tiles,
   * so leaking it to opponents reveals no hidden information.
   */
  readonly dealProof?: DiceRollProof;
}

export type StateSyncMessage = EnvelopeStateSyncMessage<DominoSnapshot>;

// --- Unions ---

export type ClientMessage =
  | LobbyClientMessage
  | DominoPlayMessage
  | DominoDrawMessage
  | DominoPassMessage
  | DominoMugginsClaimMessage
  | DominoNextHandMessage
  | DominoGameOverMessage
  | StateSyncMessage;

export type ServerMessage =
  | LobbyServerMessage
  | DominoPlayMessage
  | DominoDrawMessage
  | DominoPassMessage
  | DominoMugginsClaimMessage
  | DominoNextHandMessage
  | DominoGameOverMessage
  | StateSyncMessage;

// --- Snapshot serializer / restore ---

/**
 * Build a `DominoSnapshot` from a `GameStateData` + per-arm turn state and
 * (optionally) the verifiable-mode proof for the current hand. The caller is
 * the server (which has the unfiltered state); `getSeatView` then strips
 * opponents' tiles per recipient before send. The proof is not part of the
 * filtered private state — it rides through untouched.
 */
export function snapshotFromState(
  state: GameStateData,
  chainTurns: readonly (readonly Turn[])[],
  dealProof?: DiceRollProof,
): DominoSnapshot {
  return {
    ruleSetId: state.ruleSet.id as VariantId,
    playerCount: state.playerCount,
    pointGoal: state.ruleSet.pointGoal,
    mugginsStrict: state.ruleSet.mugginsStrict,
    opening13: state.ruleSet.opening13,
    baldFinish: state.ruleSet.baldFinish,
    seed: state.seed,
    handNumber: state.handNumber,
    handsSeatView: state.hands.map((tiles) => ({ kind: 'own', tiles })),
    boneyardCount: state.boneyard.length,
    chain: serializeChain(state.chain),
    currentSeat: state.currentSeat,
    leaderSeat: state.leaderSeat,
    scores: state.scores,
    teamMap: state.teamMap,
    pendingMuggins: state.pendingMuggins,
    handOver: state.handOver,
    matchOver: state.matchOver,
    lastResult: state.lastResult,
    consecutivePasses: state.consecutivePasses,
    forcedLeadTile: state.forcedLeadTile,
    carriedPenalty: state.carriedPenalty,
    lastAction: state.lastAction,
    chainTurns,
    ...(dealProof !== undefined ? { dealProof } : {}),
  };
}

export function serializeChain(chain: Chain): SerializedChain {
  return {
    lead: chain.lead,
    spinner: chain.spinner,
    arms: chain.arms.map((a) => ({ tiles: a.tiles, openValue: a.openValue })),
    activeArms: chain.activeArms,
  };
}

export function deserializeChain(snap: SerializedChain): Chain {
  return {
    lead: snap.lead,
    spinner: snap.spinner,
    arms: snap.arms.map((a) => ({ tiles: a.tiles, openValue: a.openValue })),
    activeArms: snap.activeArms,
  };
}

/**
 * Filter a snapshot for a given seat — redact every other seat's hand to a
 * `{ count }` stub. The server calls this per recipient inside `getSeatView`
 * before send. Boneyard composition is already stripped to `boneyardCount`
 * at construction so no further work is needed there.
 */
export function filterSnapshotForSeat(snapshot: DominoSnapshot, seat: number): DominoSnapshot {
  return {
    ...snapshot,
    handsSeatView: snapshot.handsSeatView.map((view, i) => {
      if (i === seat) return view;
      const count = view.kind === 'own' ? view.tiles.length : view.count;
      return { kind: 'hidden', count };
    }),
  };
}
